import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Plus, Edit, Trash2, Tag, User, Link as LinkIcon, X, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: string;
  tags: string[];
  published: boolean;
  views: number;
  createdAt: string;
  featuredImage?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// ✅ Feature flag (same pattern as public blog page)
// Enable only when backend blog admin endpoints exist.
const ENABLE_ADMIN_BLOG_API =
  (process.env.REACT_APP_ENABLE_ADMIN_BLOG_API || 'false').toLowerCase() === 'true';

const AdminBlogs: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    author: '',
    tags: '',
    featuredImage: '',
    published: false,
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // ✅ Do NOT call backend if it’s not implemented yet
    if (!ENABLE_ADMIN_BLOG_API) {
      setBlogs([]);
      setLoading(false);
      return;
    }

    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      if (!token) {
        toast.error('Not logged in. Please login again.');
        navigate('/admin/login');
        return;
      }

      // ✅ Admin blog list endpoint
      const response = await axios.get(`${API_BASE_URL}/api/admin/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });

      const list: BlogPost[] = Array.isArray(response.data) ? response.data : response.data?.items || [];
      setBlogs(list);
    } catch (error: any) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || error?.message;
      console.error('Admin blogs fetch failed:', { status, msg, data: error?.response?.data });

      if (status === 401) toast.error('Session expired. Please login again.');
      else toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ENABLE_ADMIN_BLOG_API) {
      toast.error('Blog API is not enabled yet (backend not connected).');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Not logged in. Please login again.');
        navigate('/admin/login');
        return;
      }

      const computedSlug =
        formData.slug ||
        formData.title
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

      const blogData = {
        ...formData,
        slug: computedSlug,
        tags: formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      };

      if (editingBlog) {
        await axios.put(`${API_BASE_URL}/api/admin/blog/posts/${editingBlog._id}`, blogData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/blog/posts`, blogData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        toast.success('Blog created successfully');
      }

      setShowForm(false);
      setEditingBlog(null);
      setSlugManuallyEdited(false);
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        author: '',
        tags: '',
        featuredImage: '',
        published: false,
      });

      fetchBlogs();
    } catch (error: any) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || error?.message;
      console.error('Save blog failed:', { status, msg, data: error?.response?.data });

      if (status === 401) toast.error('Session expired. Please login again.');
      else toast.error('Failed to save blog');
    }
  };

  const handleEdit = (blog: BlogPost) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      excerpt: blog.excerpt,
      author: blog.author,
      tags: blog.tags.join(', '),
      featuredImage: blog.featuredImage || '',
      published: blog.published,
    });
    setSlugManuallyEdited(true);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    if (!ENABLE_ADMIN_BLOG_API) {
      toast.error('Blog API is not enabled yet (backend not connected).');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Not logged in. Please login again.');
        navigate('/admin/login');
        return;
      }

      await axios.delete(`${API_BASE_URL}/api/admin/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });

      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error: any) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || error?.message;
      console.error('Delete blog failed:', { status, msg, data: error?.response?.data });

      if (status === 401) toast.error('Session expired. Please login again.');
      else toast.error('Failed to delete blog');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-all">
                <ArrowLeft size={20} />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Blog Management</h1>
                  <p className="text-blue-200 text-xs hidden sm:block">Create and manage blog posts</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingBlog(null);
                  setSlugManuallyEdited(false);
                  setFormData({
                    title: '',
                    slug: '',
                    content: '',
                    excerpt: '',
                    author: '',
                    tags: '',
                    featuredImage: '',
                    published: false,
                  });
                }}
                className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-white text-blue-900 font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg text-sm sm:text-base"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Blog Post</span>
                <span className="sm:hidden">New</span>
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        {!ENABLE_ADMIN_BLOG_API && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-xl p-4 mb-6">
            <div className="font-extrabold mb-1">Blog admin API disabled</div>
            <div className="text-sm">
              The blog admin endpoints are ready, but API calls are currently disabled.
              <span className="font-semibold"> No API calls are being made</span> to prevent errors.
              <br />
              To enable blog management, set{' '}
              <code className="px-1 py-0.5 bg-white/60 border border-yellow-200 rounded">REACT_APP_ENABLE_ADMIN_BLOG_API=true</code>{' '}
              in your Vercel environment variables.
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading blogs...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {blogs.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center text-gray-600">
                No blog posts yet.
              </div>
            ) : (
              blogs.map((blog) => (
                <div key={blog._id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-blue-900">{blog.title}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            blog.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {blog.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{blog.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>By {blog.author}</span>
                        <span>•</span>
                        <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{blog.views} views</span>
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {blog.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(blog)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(blog._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Blog Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[60] overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-5xl w-full my-4 sm:my-8 p-0 shadow-2xl overflow-hidden">
              {/* Modern Header */}
              <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-4 sm:px-6 lg:px-8 py-6 sm:py-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-5 rounded-full -ml-16 -mb-16"></div>
                <div className="relative z-10 flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 sm:w-10 h-8 sm:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                      </div>
                      <span className="text-blue-100 text-xs sm:text-sm font-semibold uppercase tracking-wide hidden sm:inline">
                        Blog Management
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white">
                      {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
                    </h2>
                    <p className="text-blue-100 text-xs sm:text-sm mt-1 sm:mt-2 hidden sm:block">
                      {editingBlog ? 'Update your blog post details' : 'Share your insights with the world'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-white hover:bg-white hover:bg-opacity-10 p-2 rounded-lg transition-all flex-shrink-0"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 lg:p-8 space-y-4 sm:space-y-6 md:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
                  <div className="md:col-span-2 space-y-3 sm:space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">📝 Title</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => {
                            const title = e.target.value;
                            const autoSlug = title
                              .toLowerCase()
                              .trim()
                              .replace(/\s+/g, '-')
                              .replace(/[^a-z0-9-]/g, '');
                            setFormData({
                              ...formData,
                              title,
                              slug: !slugManuallyEdited ? autoSlug : formData.slug,
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all bg-gray-50 hover:bg-white"
                          placeholder="Enter an engaging title for your blog post"
                          required
                        />
                        <div className="flex justify-between mt-1 text-xs text-gray-500">
                          <span>Give your blog a concise, descriptive title.</span>
                          <span className="font-medium">{formData.title.length}/120</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Slug (URL)</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <LinkIcon size={16} />
                        </div>
                        <input
                          type="text"
                          value={formData.slug}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                            });
                            setSlugManuallyEdited(true);
                          }}
                          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          placeholder="auto-generated-from-title"
                        />
                        <div className="mt-1 text-xs text-gray-500">Final URL: /blog/{formData.slug || 'your-slug'}</div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">✨ Excerpt</label>
                      <textarea
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all bg-gray-50 hover:bg-white resize-none"
                        placeholder="A brief summary of your blog post (shown in listings)"
                      />
                      <div className="flex justify-between mt-1 text-xs text-gray-500">
                        <span>Short summary shown in lists.</span>
                        <span className="font-medium">{formData.excerpt.length}/300</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">📄 Content</label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <ReactQuill
                          value={formData.content}
                          onChange={(content: string) => setFormData({ ...formData, content })}
                          theme="snow"
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['blockquote', 'code-block'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ header: [1, 2, 3, false] }],
                              ['link'],
                              ['clean'],
                            ],
                          }}
                          placeholder="Write your blog post content here..."
                          className="h-64 sm:h-80 text-sm"
                        />
                      </div>
                      <div className="flex justify-end mt-2 text-xs text-gray-500 font-medium">
                        {formData.content.replace(/<[^>]*>/g, '').length} characters
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-1 space-y-3 sm:space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">👤 Author</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <User size={16} />
                        </div>
                        <input
                          type="text"
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all bg-gray-50 hover:bg-white"
                          placeholder="Blog author name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">🏷️ Tags</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <Tag size={16} />
                        </div>
                        <input
                          type="text"
                          value={formData.tags}
                          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all bg-gray-50 hover:bg-white"
                          placeholder="Security, Vulnerability, Cloud"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.tags
                          .split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((t, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200"
                            >
                              {t}
                            </span>
                          ))}
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="published"
                          checked={formData.published}
                          onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-600 cursor-pointer"
                        />
                        <label htmlFor="published" className="text-sm font-semibold text-gray-800 cursor-pointer">
                          🚀 Publish immediately
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 border-t pt-4 sm:pt-6">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
                  >
                    {editingBlog ? '✏️ Update' : '✨ Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogs;
